import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Notification from '../components/Notification';
import { useNotification } from '../hooks/useNotification';
import ConfirmModal from '../components/ConfirmModal';

const Courses = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, courseId: null, courseTitle: '', isActive: false });
  const { notification, showError, showSuccess, hideNotification } = useNotification();

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/admin/courses');
      const courses = response.data?.data?.courses || response.data?.courses || [];
      setCourses(courses);
    } catch (error) {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActiveClick = (courseId, courseTitle, isActive) => {
    setConfirmModal({
      isOpen: true,
      courseId,
      courseTitle,
      isActive,
    });
  };

  const handleToggleActive = async () => {
    try {
      await api.patch(`/admin/courses/${confirmModal.courseId}`, { isActive: !confirmModal.isActive });
      setConfirmModal({ isOpen: false, courseId: null, courseTitle: '', isActive: false });
      fetchCourses();
      showSuccess(`Course ${confirmModal.isActive ? 'deactivated' : 'activated'} successfully`);
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to update course status');
      setConfirmModal({ isOpen: false, courseId: null, courseTitle: '', isActive: false });
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">Course Management</h1>
        <p className="text-gray-600">Manage courses, modules, and lesson content</p>
      </div>

      <div className="space-y-4">
        {courses.map((course) => (
          <div
            key={course.id}
            className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h3 className="text-xl font-bold text-gray-800">{course.title}</h3>
                  {course.isActive ? (
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                      Active
                    </span>
                  ) : (
                    <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-semibold">
                      Inactive
                    </span>
                  )}
                </div>
                {course.description && (
                  <p className="text-gray-600 mb-4">{course.description}</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Individual Price:</span>
                    <p className="font-semibold text-gray-800">
                      ${parseFloat(course.individualPrice).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Bulk Price:</span>
                    <p className="font-semibold text-gray-800">
                      ${parseFloat(course.bulkPrice).toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-500">Estimated Hours:</span>
                    <p className="font-semibold text-gray-800">{course.estimatedHours || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Modules:</span>
                    <p className="font-semibold text-gray-800">
                      {course.modules?.length || 0} modules
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex flex-col space-y-2 ml-4">
                <button
                  onClick={() => handleToggleActiveClick(course.id, course.title, course.isActive)}
                  className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                    course.isActive
                      ? 'bg-red-100 text-red-700 hover:bg-red-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {course.isActive ? 'Deactivate' : 'Activate'}
                </button>
                <button
                  onClick={() => navigate(`/courses/${course.id}`)}
                  className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primaryDark transition-colors"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {courses.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl shadow-md">
          <p className="text-gray-500">No courses found</p>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, courseId: null, courseTitle: '', isActive: false })}
        onConfirm={handleToggleActive}
        title={confirmModal.isActive ? 'Deactivate Course' : 'Activate Course'}
        message={`Are you sure you want to ${confirmModal.isActive ? 'deactivate' : 'activate'} "${confirmModal.courseTitle}"? ${confirmModal.isActive ? 'The course will no longer be available to users.' : 'The course will become available to users.'}`}
        confirmText={confirmModal.isActive ? 'Deactivate' : 'Activate'}
        cancelText="Cancel"
        variant={confirmModal.isActive ? 'danger' : 'default'}
      />

      {/* Notification */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={hideNotification}
          duration={notification.duration}
        />
      )}
    </div>
  );
};

export default Courses;



